// =================================================================
// Govern360 — Entra API (shared)
// One Graph connection, consumed by: AI-policy scoping, Deploy, and the
// read-only Conditional Access card.
//
// READ-MOSTLY by design. The only Graph WRITES Govern360 ever does are none:
// we read groups, read CA posture, and resolve membership. Deployment + CA
// changes are applied by the customer's IT. This keeps the consent footprint
// to read scopes (Group.Read.All, Policy.Read.All, DeviceManagement*.Read.All).
// =================================================================

import { supabase } from '@govern360/shared';
import { graphGroupSearch } from './entraGraph.js';
import { callFn } from './netlifyFn.js';

// Active org (== tenant_id). entra_connections is per-org; RLS lets a
// multi-org user see all their orgs' rows, so scope reads to the active one.
async function activeTenant() {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return null;
  const { data } = await supabase
    .from('profiles').select('primary_tenant_id').eq('id', user.id).maybeSingle();
  return data?.primary_tenant_id || null;
}

// ---- connection status ------------------------------------------
export async function getEntraConnection() {
  const tenantId = await activeTenant();
  if (!tenantId) return null;
  // Newest row wins. Do NOT use .maybeSingle() - it throws when more than one
  // entra_connections row is visible (e.g. multiple orgs). Order + limit(1).
  const { data, error } = await supabase
    .from('entra_connections')
    .select('*')
    .eq('tenant_id', tenantId)
    .order('updated_at', { ascending: false, nullsFirst: false })
    .limit(1);
  if (error) throw error;
  const row = Array.isArray(data) ? (data[0] || null) : (data || null);
  if (!row) return null;
  // Normalize: expose a .connected boolean so consumers do not re-derive it.
  return { ...row, connected: row.status === 'connected' };
}

export function hasScope(conn, scope) {
  return !!conn && Array.isArray(conn.scopes) && conn.scopes.includes(scope);
}

// Kick off the consent flow. PHASE 2: implement `graph-connect` Edge Function
// that returns the admin-consent URL for the read-only app registration.
export async function startEntraConnect() {
  const { data, error } = await supabase.functions.invoke('graph-connect', { body: {} });
  if (error) throw error;
  return data?.consent_url || null;
}

// ---- group search (cache first, Graph fallback) -----------------
export async function searchGroups(query) {
  // 1) cache
  const { data: cached } = await supabase
    .from('entra_groups')
    .select('entra_group_id, display_name, member_count')
    .ilike('display_name', `%${query}%`)
    .limit(10);
  if (cached && cached.length) {
    return cached.map((g) => ({ id: g.entra_group_id, displayName: g.display_name, memberCount: g.member_count }));
  }
  // 2) live Graph (Netlify function). Returns null on failure -> manual entry.
  return await graphGroupSearch(query);
}

// ---- generic binding CRUD (parameterized by table) --------------
// table is 'ai_policy_group_bindings' (this batch) or 'deploy_group_bindings'.
export async function listBindings(table, policyId) {
  const { data, error } = await supabase
    .from(table).select('*').eq('policy_id', policyId).order('kind');
  if (error) throw error;
  return data || [];
}

export async function addBinding(table, tenantId, policyId, kind, group) {
  const { data, error } = await supabase.from(table).insert({
    tenant_id: tenantId, policy_id: policyId, kind,
    entra_group_id: group.id, display_name: group.displayName,
    member_count: group.memberCount ?? null,
  }).select().single();
  if (error) throw error;
  return data;
}

export async function removeBinding(table, id) {
  const { error } = await supabase.from(table).delete().eq('id', id);
  if (error) throw error;
}

// ---- conditional access (read only) -----------------------------
export async function listConditionalAccess() {
  const { data, error } = await supabase
    .from('conditional_access_policies')
    .select('*')
    .order('synced_at', { ascending: false });
  if (error) throw error;
  return data || [];
}

// PHASE 2: graph-ca-sync Edge Function reads CA policies (Policy.Read.All),
// flags which target AI apps, and upserts the cache. NEVER writes CA.
export async function syncConditionalAccess() {
  const { data, error } = await supabase.functions.invoke('graph-ca-sync', { body: {} });
  if (error) throw error;
  return data;
}

// A recommended CA policy we SHOW but never apply. Pure helper.
export function recommendedAiAccessPolicy() {
  return {
    name: 'Require compliant device + MFA for AI tools',
    rationale:
      'Gates access to sanctioned AI tools behind a managed, compliant device and MFA — ' +
      'so prompt-level governance (Data Shield) only ever runs on trusted endpoints.',
    conditions: 'Cloud apps: your sanctioned AI tool app registrations',
    grants: ['Require multifactor authentication', 'Require device to be marked compliant'],
    apply_note:
      'Govern360 does not create Conditional Access policies. An Entra admin applies this ' +
      'in Entra > Protection > Conditional Access. We surface it here as a recommendation.',
  };
}

// Identity resolver sync: pulls members of policy-referenced Entra groups into
// the membership cache (entra_user_groups). Mirrors syncConditionalAccess.
export async function syncIdentity(tenantId) {
  const { data, error } = await supabase.functions.invoke('graph-sync-identity', {
    body: { tenant_id: tenantId },
  });
  if (error) throw error;
  return data;
}

// Sync now: pull the latest directory snapshot (users, groups) from Microsoft
// via the unified Entra connection. Netlify graph-sync-users (owner/admin,
// enforced server-side) returns { ok, users_synced?, ... } or an honest
// needs_connection / needs_consent.
export async function syncEntraDirectory() {
  return callFn('graph-sync-users', {});
}
